import React, { useState, useEffect, useCallback } from 'react';
import {
  Form,
  Input,
  InputNumber,
  Switch,
  Button,
  Card,
  Row,
  Col,
  Divider,
  Alert,
  Spin,
  message,
  Collapse,
  Tooltip,
  Tabs
} from 'antd';
import {
  SettingOutlined,
  PlayCircleOutlined,
  InfoCircleOutlined,
  ThunderboltOutlined
} from '@ant-design/icons';
import { simulationAPI } from '../services/api';
import MonteCarloResults from './MonteCarloResults';
import MonteCarloCharts from './MonteCarloCharts';
import TestCaseManager from './TestCaseManager';
import ResultsDisplay from './ResultsDisplay';

const { Panel } = Collapse;

const SimulationForm = ({ onSimulationComplete, onSimulationStart }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [betLimitingEnabled, setBetLimitingEnabled] = useState(false);
  const [treasuryRefillsEnabled, setTreasuryRefillsEnabled] = useState(false);

  // Tab state
  const [activeTab, setActiveTab] = useState('single');
  
  // Single run results
  const [singleRunResult, setSingleRunResult] = useState(null);

  // Monte Carlo UI state
  const [mcMode, setMcMode] = useState('engine');
  const [mcSamples, setMcSamples] = useState(200);
  const [mcParallel, setMcParallel] = useState(true);
  const [mcLoading, setMcLoading] = useState(false);
  const [mcSummary, setMcSummary] = useState(null);
  const [mcSamplesData, setMcSamplesData] = useState(null);

  const loadDefaultConfig = useCallback(async () => {
    try {
      const config = await simulationAPI.getDefaultConfig();
      form.setFieldsValue(config);
      setBetLimitingEnabled(config.enable_bet_limiting);
      setTreasuryRefillsEnabled(config.enable_treasury_refills);
      message.success('Default configuration loaded');
    } catch (error) {
      message.error(error.message);
    }
  }, [form]);

  useEffect(() => {
    loadDefaultConfig();
  }, [loadDefaultConfig]);

  const validateConfig = async () => {
    try {
      setValidating(true);
      const values = form.getFieldsValue();
      const result = await simulationAPI.validateConfig(values);
      setValidationResult(result);
      
      if (result.valid) {
        message.success('Configuration is valid');
      } else {
        message.error(`Errors found: ${result.errors.join(', ')}`);
      }
    } catch (error) {
      message.error(error.message);
    } finally {
      setValidating(false);
    }
  };

  const quickTest = async () => {
    try {
      setLoading(true);
      const result = await simulationAPI.quickTest();
      message.success('Quick test completed successfully');
      console.log('Quick test result:', result);
    } catch (error) {
      message.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const runSimulation = async () => {
    try {
      const values = await form.validateFields();
      
      // Check configuration before starting
      const validation = await simulationAPI.validateConfig(values);
      if (!validation.valid) {
        message.error(`Configuration contains errors: ${validation.errors.join(', ')}`);
        return;
      }

      setLoading(true);
      onSimulationStart?.();
      
      const result = await simulationAPI.runSimulation(values);
      setSingleRunResult(result);
      onSimulationComplete?.(result);
      message.success('Simulation completed successfully');
    } catch (error) {
      message.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (value) => {
    return value ? value.toLocaleString('ru-RU') : '';
  };

  const handleLoadTestCase = async (testCase) => {
    try {
      // Load configuration into form
      form.setFieldsValue(testCase.config);
      setBetLimitingEnabled(testCase.config.enable_bet_limiting);
      setTreasuryRefillsEnabled(testCase.config.enable_treasury_refills);
      
      // Load Monte Carlo results if available
      if (testCase.monte_carlo_results) {
        setMcSummary(testCase.monte_carlo_results.summary);
        setMcSamplesData(testCase.monte_carlo_results.samples);
      }
      
      message.success(`Loaded test case: ${testCase.name}`);
    } catch (error) {
      message.error(error.message);
    }
  };

  return (
    <>
    <Card 
      title={
        <span>
          <SettingOutlined style={{ marginRight: 8 }} />
          Simulation Settings
        </span>
      }
      extra={
        <Tooltip title="Quick test with minimal parameters">
          <Button 
            icon={<ThunderboltOutlined />}
            onClick={quickTest}
            loading={loading}
            type="dashed"
            size="small"
          >
            Quick Test
          </Button>
        </Tooltip>
      }
    >
      <Form
        form={form}
        layout="vertical"
        onValuesChange={(changedValues) => {
          if ('enable_bet_limiting' in changedValues) {
            setBetLimitingEnabled(changedValues.enable_bet_limiting);
          }
          if ('enable_treasury_refills' in changedValues) {
            setTreasuryRefillsEnabled(changedValues.enable_treasury_refills);
          }
        }}
      >
        <Collapse defaultActiveKey={['basic']} ghost>
          {/* Basic Parameters */}
          <Panel header="Basic Parameters" key="basic">
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Odds per Stage"
                  name="base_odds_per_leg"
                  tooltip="Base odds for each parlay stage"
                >
                  <InputNumber min={1.01} max={3.0} step={0.01} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Win Probability"
                  name="probability_win_per_leg"
                  tooltip="Win probability on each stage (0.5 = 50%)"
                >
                  <InputNumber min={0.1} max={0.9} step={0.01} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>
            
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Maximum Chain Length"
                  name="max_chain_length"
                  tooltip="Maximum number of stages in one bet"
                >
                  <InputNumber min={1} max={20} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Simulation Days"
                  name="simulation_days"
                  tooltip="Number of days for simulation"
                >
                  <InputNumber min={1} max={3650} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>
          </Panel>

          {/* Pool Balances */}
          <Panel header="Pool Balances" key="pools">
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  label="Safe Pool (Lumia)"
                  name="safe_pool_balance"
                  tooltip="Initial balance of safe pool"
                >
                  <InputNumber 
                    min={0} 
                    step={1000} 
                    style={{ width: '100%' }}
                    formatter={formatNumber}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  label="Medium Pool (Lumia)"
                  name="medium_pool_balance"
                  tooltip="Initial balance of medium pool"
                >
                  <InputNumber 
                    min={0} 
                    step={1000} 
                    style={{ width: '100%' }}
                    formatter={formatNumber}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  label="Risky Pool (Lumia)"
                  name="risky_pool_balance"
                  tooltip="Initial balance of risky pool"
                >
                  <InputNumber 
                    min={0} 
                    step={1000} 
                    style={{ width: '100%' }}
                    formatter={formatNumber}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  label="Treasury (Lumia)"
                  name="initial_treasury_balance"
                  tooltip="Initial balance of treasury"
                >
                  <InputNumber 
                    min={0} 
                    step={1000} 
                    style={{ width: '100%' }}
                    formatter={formatNumber}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  label="Jackpots (Lumia)"
                  name="initial_jackpots_balance"
                  tooltip="Initial balance of jackpots"
                >
                  <InputNumber 
                    min={0} 
                    step={1000} 
                    style={{ width: '100%' }}
                    formatter={formatNumber}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  label="Referrals (Lumia)"
                  name="initial_referrals_balance"
                  tooltip="Initial balance of referrals"
                >
                  <InputNumber 
                    min={0} 
                    step={1000} 
                    style={{ width: '100%' }}
                    formatter={formatNumber}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Panel>

          {/* Simulation Parameters */}
          <Panel header="Simulation Parameters" key="simulation">
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Minimum Games per Day"
                  name="min_daily_games"
                  tooltip="Minimum number of games per day"
                >
                  <InputNumber min={1} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Maximum Games per Day"
                  name="max_daily_games"
                  tooltip="Maximum number of games per day"
                >
                  <InputNumber min={1} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Minimum Bet (Lumia)"
                  name="min_bet_amount"
                  tooltip="Minimum bet amount"
                >
                  <InputNumber min={0} step={1} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Maximum Bet (Lumia)"
                  name="max_bet_amount"
                  tooltip="Maximum bet amount"
                >
                  <InputNumber min={0} step={10} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>
          </Panel>

          {/* Pool Protection */}
          <Panel header="Pool Protection" key="protection">
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Enable Bet Limiting"
                  name="enable_bet_limiting"
                  valuePropName="checked"
                  tooltip="Limits maximum stake based on pool balance to prevent excessive payouts"
                >
                  <Switch />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Enable Treasury Refills"
                  name="enable_treasury_refills"
                  valuePropName="checked"
                  tooltip="Automatically refills pools from treasury when they reach critical levels"
                >
                  <Switch />
                </Form.Item>
              </Col>
            </Row>

            {(betLimitingEnabled || treasuryRefillsEnabled) && (
              <>
                <Row gutter={16}>
                  {betLimitingEnabled && (
                    <Col span={12}>
                      <Form.Item
                        label="Max % total payout from Pool"
                        name="max_bet_percentage_of_pool"
                        tooltip="Maximum bet as percentage of current pool balance"
                      >
                        <InputNumber min={0} max={1} step={0.01} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                  )}
                  {treasuryRefillsEnabled && (
                    <Col span={12}>
                      <Form.Item
                        label="Critical Threshold"
                        name="critical_pool_threshold"
                        tooltip="Pool is considered critical when balance is below this percentage"
                      >
                        <InputNumber min={0} max={1} step={0.01} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                  )}
                </Row>

                <Row gutter={16}>
                  {treasuryRefillsEnabled && (
                    <Col span={12}>
                      <Form.Item
                        label="Refill to %"
                        name="refill_pool_to_percentage"
                        tooltip="Refill pools to specified percentage of initial balance"
                      >
                        <InputNumber min={0} max={1} step={0.01} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                  )}
                  {betLimitingEnabled && (
                    <Col span={12}>
                      <Form.Item
                        label="Min. Bet Rejection Threshold"
                        name="min_bet_threshold_for_rejection"
                        tooltip="Minimum bet amount for rejection when pool balances are low"
                      >
                        <InputNumber min={0} step={1} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                  )}
                </Row>
              </>
            )}
          </Panel>

          {/* House Edge Distribution */}
          <Panel header="House Edge Distribution" key="house_edge">
            <Alert
              message="Sum of all percentages must equal 100%"
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
            
            <Alert
              message="House Edge Distribution"
              description="Configure how House Edge is distributed when players win vs lose. House Edge is calculated from base odds per stage."
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />

            {/* When Player Wins */}
            <div style={{ marginBottom: 16 }}>
              <h4 style={{ color: '#52c41a', marginBottom: 12 }}>When Player Wins</h4>
              <Row gutter={16}>
                <Col span={6}>
                  <Form.Item
                    label="Pools (%)"
                    name="he_split_pools_on_win"
                    tooltip="Share of HE that remains in pools when player wins"
                  >
                    <InputNumber min={0} max={1} step={0.01} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    label="Protocol (%)"
                    name="he_split_protocol_on_win"
                    tooltip="Share of HE that goes to treasury when player wins"
                  >
                    <InputNumber min={0} max={1} step={0.01} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    label="Jackpots (%)"
                    name="he_split_jackpots_on_win"
                    tooltip="Share of HE that goes to jackpots when player wins"
                  >
                    <InputNumber min={0} max={1} step={0.01} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    label="Referrals (%)"
                    name="he_split_referrals_on_win"
                    tooltip="Share of HE that goes to referrals when player wins"
                  >
                    <InputNumber min={0} max={1} step={0.01} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>
            </div>

            {/* When Player Loses */}
            <div style={{ marginBottom: 16 }}>
              <h4 style={{ color: '#ff4d4f', marginBottom: 12 }}>When Player Loses</h4>
              <Row gutter={16}>
                <Col span={6}>
                  <Form.Item
                    label="Pools (%)"
                    name="he_split_pools_on_loss"
                    tooltip="Share of HE that remains in pools when player loses"
                  >
                    <InputNumber min={0} max={1} step={0.01} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    label="Protocol (%)"
                    name="he_split_protocol_on_loss"
                    tooltip="Share of HE that goes to treasury when player loses"
                  >
                    <InputNumber min={0} max={1} step={0.01} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    label="Jackpots (%)"
                    name="he_split_jackpots_on_loss"
                    tooltip="Share of HE that goes to jackpots when player loses"
                  >
                    <InputNumber min={0} max={1} step={0.01} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    label="Referrals (%)"
                    name="he_split_referrals_on_loss"
                    tooltip="Share of HE that goes to referrals when player loses"
                  >
                    <InputNumber min={0} max={1} step={0.01} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>
            </div>
          </Panel>

          {/* Simulation Control */}
          <Panel header="Simulation Control" key="control">
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Use Random Seed"
                  name="enable_random_seed"
                  valuePropName="checked"
                  tooltip="Use fixed seed for reproducible results"
                >
                  <Switch />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Seed Value"
                  name="random_seed"
                  tooltip="Seed value for random number generation"
                >
                  <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>
          </Panel>
        </Collapse>

        <Divider />

        {/* Control Buttons */}
        <Row gutter={16} justify="center">
          <Col>
            <Button 
              onClick={loadDefaultConfig}
              icon={<InfoCircleOutlined />}
            >
              Load Default
            </Button>
          </Col>
          <Col>
            <Button 
              onClick={validateConfig}
              loading={validating}
              icon={<InfoCircleOutlined />}
            >
              Validate Configuration
            </Button>
          </Col>
        </Row>

        {/* Validation Result */}
        {validationResult && (
          <Alert
            message={validationResult.valid ? "Configuration is valid" : "Errors found"}
            description={
              <div>
                {validationResult.errors.length > 0 && (
                  <div>
                    <strong>Errors:</strong>
                    <ul>
                      {validationResult.errors.map((error, index) => (
                        <li key={index}>{error}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {validationResult.warnings.length > 0 && (
                  <div>
                    <strong>Warnings:</strong>
                    <ul>
                      {validationResult.warnings.map((warning, index) => (
                        <li key={index}>{warning}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            }
            type={validationResult.valid ? "success" : "error"}
            showIcon
            style={{ marginTop: 16 }}
          />
        )}
      </Form>
    </Card>

    {/* Simulation Tabs */}
    <Card style={{ marginTop: 16 }}>
      <Tabs 
        activeKey={activeTab} 
        onChange={setActiveTab}
        items={[
          {
            key: 'single',
            label: (
              <span>
                <PlayCircleOutlined style={{ marginRight: 8 }} />
                Single Run
              </span>
            ),
            children: (
              <div>
                <Row gutter={16} justify="center" style={{ marginBottom: 16 }}>
                  <Col>
                    <Button 
                      type="primary"
                      size="large"
                      onClick={runSimulation}
                      loading={loading}
                      icon={<PlayCircleOutlined />}
                    >
                      Run Simulation
                    </Button>
                  </Col>
                </Row>
                
                {singleRunResult && (
                  <ResultsDisplay simulationResult={singleRunResult} />
                )}
                
              </div>
            )
          },
          {
            key: 'monte-carlo',
            label: (
              <span>
                <ThunderboltOutlined style={{ marginRight: 8 }} />
                Monte Carlo
              </span>
            ),
            children: (
              <div>
                <Row gutter={12} align="middle" style={{ marginBottom: 16 }}>
                  <Col>
                    <label>
                      Mode:&nbsp;
                      <select value={mcMode} onChange={(e) => setMcMode(e.target.value)}>
                        <option value="engine">Engine</option>
                      </select>
                    </label>
                  </Col>
                  <Col>
                    <label>
                      Samples:&nbsp;
                      <InputNumber min={1} max={20000} value={mcSamples} onChange={(v) => setMcSamples(Number(v || 0))} />
                    </label>
                  </Col>
                  <Col>
                    <label>
                      Parallel:&nbsp;
                      <Switch checked={mcParallel} onChange={(v) => setMcParallel(v)} />
                    </label>
                  </Col>
                  <Col>
                    <Button type="primary" loading={mcLoading} onClick={async () => {
                      try {
                        setMcLoading(true);
                        setMcSummary(null);
                        const values = await form.validateFields();
                        const payload = { config: values, pool_distribution: null };
                        const data = await simulationAPI.runMonteCarlo(payload, { samples: mcSamples, parallel: mcParallel, mode: mcMode });
                        setMcSummary(data.summary);
                        setMcSamplesData(data.samples);
                        message.success('Monte Carlo completed');
                      } catch (e) {
                        message.error(e.message);
                      } finally {
                        setMcLoading(false);
                      }
                    }}>
                      Run Monte Carlo
                    </Button>
                  </Col>
                </Row>

                {mcSummary && (
                  <div>
                    <Alert type="info" showIcon message={`Samples: ${mcSummary.samples}, Parallel: ${mcSummary.parallel}`} style={{ marginBottom: 12 }} />
                    <MonteCarloResults summary={mcSummary} />
                    <div style={{ marginTop: 16 }}>
                      <MonteCarloCharts samples={mcSamplesData} />
                    </div>
                  </div>
                )}
              </div>
            )
          }
        ]}
      />
    </Card>

    {/* Test Cases Manager */}
    <TestCaseManager
      currentConfig={form.getFieldsValue()}
      currentPoolDistribution={null}
      currentMonteCarloResults={mcSummary ? { summary: mcSummary, samples: mcSamplesData } : null}
      onLoadTestCase={handleLoadTestCase}
    />
    </>
  );
};

export default SimulationForm;
